import { ColumnConfig } from 'src/components/Overlays/ColumnDisplayMenu'
import { CustomFieldStatus } from 'src/graphql/types/customFields'

export const COLUMN_KEYS = {
  SELECT: 'select',
  APPLICANT: 'applicant',
  POSITION: 'position',
  LEAD_SOURCE: 'leadSource',
  EMPLOYEES: 'employees',
  CONTACT: 'contact',
  EMAIL: 'email',
  CALLS: 'calls',
  MISSED_CALLS: 'missedCalls',
  APPLIED: 'applied',
  LAST_CONTACT: 'lastContact',
  HIRED: 'hired',
  TERMINATED: 'terminated',
  DISQUALIFIED: 'disqualified',
  NOT_INTERESTED: 'notInterested',
  AVAILABLE_FOR_WORK: 'availableForWork',
  STAGE: 'stage',
  EXPERIENCE: 'experience',
  LOCATION: 'location',
  ACTIONS: 'actions',
} as const

export const DEFAULT_COLUMN_ORDER = [
  COLUMN_KEYS.SELECT,
  COLUMN_KEYS.APPLICANT,
  COLUMN_KEYS.POSITION,
  COLUMN_KEYS.LEAD_SOURCE,
  COLUMN_KEYS.EMPLOYEES,
  COLUMN_KEYS.CONTACT,
  COLUMN_KEYS.EMAIL,
  COLUMN_KEYS.CALLS,
  COLUMN_KEYS.MISSED_CALLS,
  COLUMN_KEYS.APPLIED,
  COLUMN_KEYS.LAST_CONTACT,
  COLUMN_KEYS.HIRED,
  COLUMN_KEYS.TERMINATED,
  COLUMN_KEYS.DISQUALIFIED,
  COLUMN_KEYS.NOT_INTERESTED,
  COLUMN_KEYS.STAGE,
  COLUMN_KEYS.EXPERIENCE,
  COLUMN_KEYS.LOCATION,
  COLUMN_KEYS.ACTIONS,
  COLUMN_KEYS.AVAILABLE_FOR_WORK,
]

export const DEFAULT_COLUMN_VISIBILITY = {
  [COLUMN_KEYS.SELECT]: true,
  [COLUMN_KEYS.APPLICANT]: true,
  [COLUMN_KEYS.POSITION]: true,
  [COLUMN_KEYS.LEAD_SOURCE]: false,
  [COLUMN_KEYS.EMPLOYEES]: false,
  [COLUMN_KEYS.CONTACT]: true,
  [COLUMN_KEYS.EMAIL]: false,
  [COLUMN_KEYS.CALLS]: true,
  [COLUMN_KEYS.MISSED_CALLS]: false,
  [COLUMN_KEYS.APPLIED]: true,
  [COLUMN_KEYS.LAST_CONTACT]: false,
  [COLUMN_KEYS.HIRED]: false,
  [COLUMN_KEYS.TERMINATED]: false,
  [COLUMN_KEYS.DISQUALIFIED]: false,
  [COLUMN_KEYS.NOT_INTERESTED]: false,
  [COLUMN_KEYS.AVAILABLE_FOR_WORK]: false,
  [COLUMN_KEYS.STAGE]: true,
  [COLUMN_KEYS.EXPERIENCE]: true,
  [COLUMN_KEYS.LOCATION]: true,
  [COLUMN_KEYS.ACTIONS]: true,
}

export const createColumnConfigs = (
  columnOrder: string[],
  columnVisibility: Record<string, boolean>,
  customFields: Array<{
    customFieldId: string
    label: string
    status: CustomFieldStatus
  }> = [],
  stepColumns: Array<{ key: string; label: string }> = []
): ColumnConfig[] => {
  // Standard column configurations
  const standardConfigs = {
    applicant: {
      key: 'applicant',
      label: 'Applicant',
      visible: columnVisibility.applicant,
    },
    position: {
      key: 'position',
      label: 'Position',
      visible: columnVisibility.position,
    },
    leadSource: {
      key: 'leadSource',
      label: 'Lead Source',
      visible: columnVisibility.leadSource,
    },
    employees: {
      key: 'employees',
      label: 'Employee(s)',
      visible: columnVisibility.employees,
    },
    contact: {
      key: 'contact',
      label: 'Contact',
      visible: columnVisibility.contact,
    },
    email: {
      key: 'email',
      label: 'Email',
      visible: columnVisibility.email,
    },
    calls: {
      key: 'calls',
      label: 'Calls',
      visible: columnVisibility.calls,
    },
    missedCalls: {
      key: 'missedCalls',
      label: 'Missed Calls',
      visible: columnVisibility.missedCalls,
    },
    applied: {
      key: 'applied',
      label: 'Applied Date',
      visible: columnVisibility.applied,
    },
    lastContact: {
      key: 'lastContact',
      label: 'Last Contact Date',
      visible: columnVisibility.lastContact,
    },
    hired: {
      key: 'hired',
      label: 'Hired Date',
      visible: columnVisibility.hired,
    },
    terminated: {
      key: 'terminated',
      label: 'Terminated Date',
      visible: columnVisibility.terminated,
    },
    disqualified: {
      key: 'disqualified',
      label: 'Disqualified Date',
      visible: columnVisibility.disqualified,
    },
    notInterested: {
      key: 'notInterested',
      label: 'Not Interested Date',
      visible: columnVisibility.notInterested,
    },
    availableForWork: {
      key: 'availableForWork',
      label: 'Available for Work Date',
      visible: columnVisibility.availableForWork,
    },
    stage: {
      key: 'stage',
      label: 'Stage',
      visible: columnVisibility.stage,
    },
    experience: {
      key: 'experience',
      label: 'Experience',
      visible: columnVisibility.experience,
    },
    location: {
      key: 'location',
      label: 'Location',
      visible: columnVisibility.location,
    },
    actions: {
      key: 'actions',
      label: 'To Do',
      visible: columnVisibility.actions,
    },
  }

  // Generate custom field configurations
  // All active custom fields are displayable, regardless of isSearchable
  const customFieldConfigs = customFields
    .filter((f) => f.status === CustomFieldStatus.ACTIVE)
    .reduce((acc, field) => {
      const key = `customField_${field.customFieldId}`
      acc[key] = {
        key,
        label: field.label,
        visible: columnVisibility[key] ?? false,
        category: 'Custom Fields', // Optional grouping in display menu
      }
      return acc
    }, {})

  // Generate step column configurations (enum step types + custom steps)
  const stepColumnConfigs = stepColumns.reduce((acc, col) => {
    acc[col.key] = {
      key: col.key,
      label: col.label,
      visible: columnVisibility[col.key] ?? false,
      category: 'Steps', // Optional grouping in display menu
    }
    return acc
  }, {})

  // Merge all configs
  const allConfigs = {
    ...standardConfigs,
    ...customFieldConfigs,
    ...stepColumnConfigs,
  }

  return columnOrder
    .filter((key) => key !== 'select') // Exclude select column from display menu
    .map((key) => allConfigs[key as keyof typeof allConfigs])
    .filter(Boolean)
}

// localStorage utility functions
export const loadColumnOrder = (): string[] => {
  if (typeof window === 'undefined') return DEFAULT_COLUMN_ORDER

  try {
    const saved = localStorage.getItem('applicants-table-column-order')
    return saved ? JSON.parse(saved) : DEFAULT_COLUMN_ORDER
  } catch {
    return DEFAULT_COLUMN_ORDER
  }
}

export const saveColumnOrder = (order: string[]): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('applicants-table-column-order', JSON.stringify(order))
  }
}

export const loadColumnVisibility = (): Record<string, boolean> => {
  if (typeof window === 'undefined') return DEFAULT_COLUMN_VISIBILITY

  try {
    const saved = localStorage.getItem('applicants-table-column-visibility')
    return saved ? JSON.parse(saved) : DEFAULT_COLUMN_VISIBILITY
  } catch {
    return DEFAULT_COLUMN_VISIBILITY
  }
}

export const saveColumnVisibility = (
  visibility: Record<string, boolean>
): void => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(
      'applicants-table-column-visibility',
      JSON.stringify(visibility)
    )
  }
}

// Reset functions for testing/development
export const resetColumnPreferences = (): void => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem('applicants-table-column-order')
    localStorage.removeItem('applicants-table-column-visibility')
  }
}
